import { type Types, utilities } from '@cornerstonejs/core';
import { defaultSegmentationStateManager } from './SegmentationStateManager';
import { getNextColorLUTIndex } from './getNextColorLUTIndex';
import CORNERSTONE_COLOR_LUT from '../../constants/COLOR_LUT';
import { utilities as cornerstoneUtilities } from '@cornerstonejs/core';

const cs3dLogger = cornerstoneUtilities.logger.toolsLog.getLogger(
  'stateManagement.segmentation.addColorLUT'
);

const PREVIEW_COLOR_INDEX = 255;
const MINIMUM_COLOR_LUT_ENTRIES = PREVIEW_COLOR_INDEX + 1;

/**
 * Add a color LUT to the segmentation state manager
 * @param colorLUT - The color LUT array to add.
 * @param index - The index of the color LUT to add.
 *
 * If no index is provided, the next available index will be used.
 *
 * @returns The index of the color LUT that was added.
 */
export function addColorLUT(colorLUT: Types.ColorLUT, index?: number): number {
  const segmentationStateManager = defaultSegmentationStateManager;

  const indexToUse = index ?? getNextColorLUTIndex();
  let colorLUTToUse = [...colorLUT] as Types.ColorLUT;

  // Make sure the colorLUT always starts with [0, 0, 0, 0] for the background color
  if (!utilities.isEqual(colorLUTToUse[0], [0, 0, 0, 0])) {
    cs3dLogger.warn(
      'addColorLUT: [0, 0, 0, 0] color is not provided for the background color (segmentIndex =0), automatically adding it'
    );
    colorLUTToUse = [[0, 0, 0, 0], ...colorLUTToUse];
  }

  // Ensure each color in the LUT has 4 elements (RGBA)
  colorLUTToUse = colorLUTToUse.map((color) => {
    // @ts-expect-error - color.length is not always 3
    if (color.length === 3) {
      return [color[0], color[1], color[2], 255] as Types.Color;
    }
    return color as Types.Color;
  });

  // Preview labelmaps use index 255, so the LUT needs entries 0..255.
  if (colorLUTToUse.length < MINIMUM_COLOR_LUT_ENTRIES) {
    const missingColorLUTs = CORNERSTONE_COLOR_LUT.slice(colorLUTToUse.length);
    colorLUTToUse = [...colorLUTToUse, ...missingColorLUTs] as Types.ColorLUT;
  }

  while (colorLUTToUse.length < MINIMUM_COLOR_LUT_ENTRIES) {
    const paletteIndex =
      ((colorLUTToUse.length - 1) % (CORNERSTONE_COLOR_LUT.length - 1)) + 1;
    colorLUTToUse.push([...CORNERSTONE_COLOR_LUT[paletteIndex]] as Types.Color);
  }

  segmentationStateManager.addColorLUT(colorLUTToUse, indexToUse);

  return indexToUse;
}
